import React, { Dispatch, SetStateAction } from 'react';
import axios from '../../../modules/utils/axios.utils';
import { MACHINE_MANAGEMENT_RETRY_INTERVAL, MACHINE_MANAGEMENT_TOTAL_RETRIES } from '../../../modules/utils/env';
import { SnackBarContext } from '../../../modules/snackBar/contexts/SnackBarContext';

interface DriversAutoBulkContextInterface {
  fetchCachedDriversAutoBulk?: (machineSerial: string) => Promise<void>;
  fetchDriversAutoBulk?: (machineSerial: string, genericFilter: string, onSuccess?: () => void) => Promise<void>;
  driversRefreshedAt?: string;
  drivers?: any[];
  driversLoading?: boolean;

  fetchDriverOptions?: (machineSerial: string) => Promise<void>;
  driverOptions?: any;
  driverOptionsLoading?: any;

  createDriverAutoBulk?: (
    formValues: any,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => Promise<void>;

  updateDriverAutoBulk?: (
    formValues: any,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => Promise<void>;

  deleteDriverAutoBulk?: (
    driverId: string,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => Promise<void>;

  creatingPage?: boolean;
  setCreatingPage?: Dispatch<SetStateAction<boolean>>;
  detailPage?: boolean;
  setDetailPage?: Dispatch<SetStateAction<boolean>>;

  setDriverSubmitting?: Dispatch<SetStateAction<boolean>>;
  driverSubmitting?: boolean;

  setActiveMachine?: Dispatch<SetStateAction<Record<string, string>>>;
  activeMachine?: Record<string, string>;

  setActiveDriverAutoBulk?: Dispatch<SetStateAction<Record<string, string>>>;
  activeDriverAutoBulk?: Record<string, string>;

  handleGenericFilterChange?: (genericFilter: string) => Promise<void>;
  genericFilter?: string;
}

const DriversAutoBulkContext = React.createContext<DriversAutoBulkContextInterface>({});

const DriversAutoBulkContextConsumer = DriversAutoBulkContext.Consumer;
const DriversAutoBulkContextProvider: React.FC = ({ children }) => {
  const { showErrorSnackBar, showSuccessSnackBar, showMachineManagementSnackBar } = React.useContext(SnackBarContext);

  const [drivers, setDriversAutoBulk] = React.useState([]);
  const [driversLoading, setDriversAutoBulkLoading] = React.useState(true);
  const [driversRefreshedAt, setDriversAutoBulkRefreshedAt] = React.useState<string>();
  const [activeDriverAutoBulk, setActiveDriverAutoBulk] = React.useState({});
  const [creatingPage, setCreatingPage] = React.useState<boolean>(false);
  const [detailPage, setDetailPage] = React.useState<boolean>(false);
  const [activeMachine, setActiveMachine] = React.useState<Record<string, string>>({
    label: localStorage.getItem('machineManagementAutoBulkMachine') || 'Select A Machine',
    value: localStorage.getItem('machineManagementAutoBulkMachine') || 'Select A Machine',
  });
  const [genericFilter, setGenericFilter] = React.useState<string>('');
  const [driverOptions, setDriverOptions] = React.useState({
    driverRFIDOptions: [],
  });
  const [driverSubmitting, setDriverSubmitting] = React.useState(false);
  const [driverOptionsLoading, setDriverOptionsLoading] = React.useState(false);

  React.useEffect(() => {
    if (activeMachine?.value === 'Select A Machine') setDriversAutoBulkLoading(false);
  }, [activeMachine]);

  const fetchCachedDriversAutoBulk = async (machineSerial: string) => {
    setDriversAutoBulkLoading(true);
    setDriversAutoBulkRefreshedAt(null);
    const retryCount = 0;

    axios
      .get<string, any>(`api/autobulk/onprem/drivers/${machineSerial}/`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      })
      .then((response) => {
        setDriversAutoBulkLoading(false);
        setDriversAutoBulk(response.data.results);
        setDriversAutoBulkRefreshedAt(response.data.created_at);
      })
      .catch((error) => {
        setDriversAutoBulkLoading(false);
        setDriversAutoBulk([]);
        setDriversAutoBulkRefreshedAt(null);
      });
  };

  const fetchDriversAutoBulk = async (machineSerial: string, genericFilter: string, onSuccess?: () => void) => {
    // Don't set loading to true to keep the grid visible
    // Only clear refresh timestamp to indicate fetch in progress
    setDriversAutoBulkRefreshedAt(null);
    let retryCount = 0;

    axios
      .get<string, any>(`api/autobulk/onprem/drivers/?serial-number=${machineSerial}&generic-filter=${genericFilter}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      })
      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autobulk/onprem/drivers/${res.data.result}/`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearInterval(fetchTimer);
              // Quietly update data without loading indicator
              setDriversAutoBulk(response.data.results);
              setDriversAutoBulkRefreshedAt(response.data.created_at);
              setDriversAutoBulkLoading(false);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                setDriversAutoBulkLoading(false);
                showErrorSnackBar('Unable to load drivers');
                setDriversAutoBulk([]);
                setDriversAutoBulkRefreshedAt(null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      });
  };

  const fetchDriverOptions = async (machineSerial: string) => {
    setDriverOptionsLoading(true);

    axios
      .get<string, any>(`api/autobulk/onprem/driver_options/${machineSerial}/`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
      })
      .then((response) => {
        setDriverOptions({
          ...driverOptions,
          driverRFIDOptions: response.data.results.filter((choice) => choice.category === 'rfid'),
        });
      })
      .catch((response) => {
        setDriverOptions({
          ...driverOptions,
          driverRFIDOptions: [],
        });
      });
  };

  const createDriverAutoBulk = async (
    formValues: any,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => {
    setDriverSubmitting(true);
    // Don't set loading to true to keep the grid visible
    setDriversAutoBulkRefreshedAt(null);
    showSuccessSnackBar('Sending record to the machine.');
    let retryCount = 0;

    axios
      .post<string, any>(
        `api/autobulk/onprem/drivers/?serial-number=${activeMachine?.value}`,
        { ...formValues },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` } },
      )
      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autobulk/onprem/drivers/${res.data.result}/`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearInterval(fetchTimer);
              setDriversAutoBulkLoading(false);
              const responseMessage = response.data.results[0]?.message || 'Driver created successfully';
              const success = response.data.results[0]?.status === 'Success';

              // Don't show a snackbar notification as we're showing it in the dialog
              // This prevents duplicate messages

              // Quietly refresh the driver list in the background
              if (activeMachine?.value) {
                axios
                  .get<string, any>(
                    `api/autobulk/onprem/drivers/?serial-number=${activeMachine.value}&generic-filter=`,
                    {
                      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
                    },
                  )
                  .then((listResponse) => {
                    // Update the data without showing loading indicator
                    setDriversAutoBulk(listResponse.data.results);
                    setDriversAutoBulkRefreshedAt(listResponse.data.created_at);
                  })
                  .catch(() => {});
              }
              setDriverSubmitting(false);

              // Pass full response data to the form dialog for error handling
              const responseData = success
                ? null
                : {
                    table: response.data.results[0]?.table,
                    record_id: response.data.results[0]?.record_id,
                  };

              if (onSuccess) onSuccess(success, responseMessage, responseData);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                setDriversAutoBulkLoading(false);
                setDriverSubmitting(false);
                showErrorSnackBar('No response from machine');
                setDriversAutoBulk([]);
                setDriversAutoBulkRefreshedAt(null);
                if (onSuccess) onSuccess(false, 'No response from machine', null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      });
  };

  const updateDriverAutoBulk = async (
    formValues: any,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => {
    setDriverSubmitting(true);
    // Don't set loading to true to keep the grid visible
    setDriversAutoBulkRefreshedAt(null);
    showSuccessSnackBar('Sending update to the machine.');
    let retryCount = 0;

    axios
      .put<string, any>(
        `api/autobulk/onprem/drivers/0/?serial-number=${activeMachine?.value}`,
        { ...formValues },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` } },
      )
      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autobulk/onprem/drivers/${res.data.result}/`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearInterval(fetchTimer);
              // Don't remove existing grid data with a loading indicator
              const responseMessage = response.data.results[0]?.message || 'Driver updated successfully';
              const success = response.data.results[0]?.status === 'Success';
              setDriversAutoBulkLoading(false);

              // Don't show a snackbar notification as we're showing it in the dialog
              // This prevents duplicate messages

              // Quietly refresh the driver list in the background
              if (activeMachine?.value) {
                axios
                  .get<string, any>(
                    `api/autobulk/onprem/drivers/?serial-number=${activeMachine.value}&generic-filter=`,
                    {
                      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
                    },
                  )
                  .then((listResponse) => {
                    // Update the data without showing loading indicator
                    setDriversAutoBulk(listResponse.data.results);
                    setDriversAutoBulkRefreshedAt(listResponse.data.created_at);
                  })
                  .catch(() => {});
              }
              setDriverSubmitting(false);

              // Pass full response data to the form dialog for error handling
              const responseData = success
                ? null
                : {
                    table: response.data.results[0]?.table,
                    record_id: response.data.results[0]?.record_id,
                  };

              if (onSuccess) onSuccess(success, responseMessage, responseData);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                setDriversAutoBulkLoading(false);
                setDriverSubmitting(false);
                showErrorSnackBar('No response from machine');
                setDriversAutoBulk([]);
                setDriversAutoBulkRefreshedAt(null);
                if (onSuccess) onSuccess(false, 'No response from machine', null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      });
  };

  const deleteDriverAutoBulk = async (
    driverId: string,
    onSuccess?: (success: boolean, message?: string, responseData?: any) => void,
  ) => {
    setDriverSubmitting(true);
    setDriversAutoBulkLoading(true);
    setDriversAutoBulkRefreshedAt(null);
    let retryCount = 0;

    axios
      .delete<string, any>(
        `api/autobulk/onprem/drivers/0/?serial-number=${activeMachine?.value}`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          data: { driver_id: driverId }, // Send driver_id in the body similar to update
        } as any, // Type assertion to fix TypeScript error
      )
      .then((res) => {
        const fetchTimer = window.setInterval(() => {
          axios
            .get<string, any>(`api/autobulk/onprem/drivers/${res.data.result}/`, {
              headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
            })
            .then((response) => {
              window.clearInterval(fetchTimer);
              setDriversAutoBulkLoading(false);
              const responseMessage = response.data.results[0]?.message || 'Driver deleted successfully';
              const success = response.data.results[0]?.status === 'Success';

              // Quietly refresh the driver list in the background
              if (activeMachine?.value) {
                axios
                  .get<string, any>(
                    `api/autobulk/onprem/drivers/?serial-number=${activeMachine.value}&generic-filter=`,
                    {
                      headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
                    },
                  )
                  .then((listResponse) => {
                    // Update the data without showing loading indicator
                    setDriversAutoBulk(listResponse.data.results);
                    setDriversAutoBulkRefreshedAt(listResponse.data.created_at);
                  })
                  .catch(() => {});
              }
              setDriverSubmitting(false);

              // Extract response data for conflict handling
              const responseData = success
                ? null
                : {
                    table: response.data.results[0]?.table,
                    record_id: response.data.results[0]?.record_id,
                  };

              if (onSuccess) onSuccess(success, responseMessage, responseData);
            })
            .catch((error) => {
              retryCount += 1;

              if (retryCount > MACHINE_MANAGEMENT_TOTAL_RETRIES) {
                window.clearInterval(fetchTimer);
                setDriversAutoBulkLoading(false);
                setDriverSubmitting(false);
                showErrorSnackBar('No response from machine');
                setDriversAutoBulk([]);
                setDriversAutoBulkRefreshedAt(null);
                // Pass null for responseData when there's no machine response
                if (onSuccess) onSuccess(false, 'No response from machine', null);
              }
            });
        }, MACHINE_MANAGEMENT_RETRY_INTERVAL);
      });
  };

  const handleGenericFilterChange = async (genericFilter: string) => {
    setGenericFilter(genericFilter);
  };

  return (
    <DriversAutoBulkContext.Provider
      value={{
        fetchCachedDriversAutoBulk,
        fetchDriversAutoBulk,
        drivers,
        driversRefreshedAt,
        driversLoading,

        fetchDriverOptions,
        driverOptions,

        createDriverAutoBulk,

        updateDriverAutoBulk,
        deleteDriverAutoBulk,

        creatingPage,
        setCreatingPage,
        detailPage,
        setDetailPage,

        driverSubmitting,

        setActiveDriverAutoBulk,
        activeDriverAutoBulk,

        setActiveMachine,
        activeMachine,

        handleGenericFilterChange,
        genericFilter,
      }}
    >
      {children}
    </DriversAutoBulkContext.Provider>
  );
};

export { DriversAutoBulkContextProvider, DriversAutoBulkContextConsumer, DriversAutoBulkContext };
